/**
 * AN INVOICE'S OWN PAGE, at `/invoices/:invoiceId`, where Pay on Payments leads, a client's alone.
 * No artboard draws it (business issue 13), so it is built from drawn parts: Payments' heading and
 * card (`faithfully-kept-business/design/home/Payments.dc.html`, its first frame), Feedback's faces,
 * area chips and line (`Feedback.dc.html`), and the seam's words (ops's `docs/seams/home.md`,
 * section 5, l.709-743 at b727ec1), with the invoice ops pushed, as GET /api/invoices/:id answers
 * it. The words are in `words.ts`.
 *
 * - TOP TO BOTTOM: "Your invoice" over the invoice's day; its card, with the amount, the state ops
 *   says it is in under it (while owed, the day it is due, `owedStateOf`, as its card on Payments
 *   says it), its lines, "Total" with ops's total under them, and the day it was issued at its foot
 *   unless the heading already names that day; "How did we do?"; then the ways to pay, where ops
 *   says there is something to pay from here now. The card carries what ops's own invoice page
 *   carries, as the seam asks ("so home is the same page", ops's `docs/seams/home.md` l.740-742 at
 *   d1c7636), since the owner's walk of 2026-10-16 found this page the card on Payments again.
 * - WITH SEVERAL HOMES, its clean's home is named under the day, by the label ops typed alone
 *   (`homeNameOf`, provisional P45, as P40), in body type and never muted, as its card and its row on
 *   Payments name it, so two invoices of one day at two homes are two pages that read apart at a
 *   glance. With one home, nothing. Under them, its number, "Invoice FK-42", as ops gives it at its
 *   first send (ops's section 11.2, slice M2 of round four), in the body colour; nothing for one
 *   sent before numbering.
 * - ARRIVING BY PAY (slice M2 of round four, after the walk was replayed, where she landed at the
 *   top with nothing on screen to pay with): Pay on Payments hands the page `pay` in the history,
 *   and once the page's heading has the focus, as after every move, the ways to pay are brought to
 *   the top of the screen, clear of the nav, smoothly unless she asked for less motion, and held
 *   there while the page's own layout settles, until she does anything herself (`landOn`, after CI
 *   run 39344566493). Opened on the way she left from (`opensOn`), that way's heading takes the focus
 *   and is brought into view the same way, as after a move between the ways.
 * - ARRIVING BY A LINK OPS PUT IN FRONT OF HER (the owner's decision of 2026-10-19, ops #76,
 *   `docs/seam.md` P107; signed out, she is brought back here after the code, `lib/cameFor.ts`):
 *   `?pay=bank`, `card` or `zelle` opens the ways to pay on that way, with no focus given, and the
 *   page lands on them as by Pay, only where that way is offered for this invoice as ops has it now
 *   (`offersWay`); otherwise the invoice as usual. `?document=<id>` presses that PDF's button once,
 *   the same `invoice.document` and poll as hers, where the invoice offers it and ops says it is
 *   made (`documentOnArrival`), and the page lands on the Documents part, which otherwise says what
 *   it always says; its id is taken out of the address at once, so a reload or the way back from
 *   the PDF never asks again. Each is read once, as the page is first drawn. A person shown no money
 *   is shown no invoice: the Worker's 404 goes to Payments, and Payments' to Home.
 * - BACK FROM THE OFFICE'S PAGE, OR ONE HER TEXTED LINK MADE, SIGNED IN (ops's plan for clients
 *   reaching only home, section 7, ask 3; `docs/seam.md` P110): with an address back the contract
 *   defines (`page=`, `saves=`, `not-paid`), section 5's words for it at the top, as the page Stripe
 *   sends her back to says them above Payments, home's own line on how to pay after `not-paid` where
 *   a way to pay it is drawn below, saying to choose one (`INVOICE_HOW_TO_PAY`), their heading the
 *   page's `h1` and the invoice's an `h2` under it; above the invoice on its way, the code its door
 *   waits for and a failed read too, since they need no read. The invoice under them is as ops last
 *   pushed it, never paid till ops says so. After a
 *   payment the page notes the return for this invoice (`noteReturnTo`): as the return page does in
 *   the tab that left to pay this very invoice, and in any other tab as P35's mark, since the
 *   office's page, a page her texted link made, or a page opened from another tab comes back here
 *   (the fifth client-journey walk, which found "Thank you" over the same invoice still offering
 *   Zelle). So its ways to pay are held as on its way, and she is not offered Pay again before ops's
 *   push or the mark's day; after `not-paid` nothing is noted (`WordsBack`). A return is noted once
 *   for the page's entry in the history, which the page then holds as noted at the same address, so
 *   a reload, Back or a restored tab opening it again after ops's push let the mark go marks nothing
 *   anew, and the invoice ops offers again is offered (the review of 3efb79f).
 * - OPS DECIDES, AND HOME MARKS NOTHING PAID (CLAUDE.md binding rule 1, the seam l.362-363). The
 *   amount is what is still to pay while ops says the invoice is owed, and its total once ops says
 *   paid; a state this build has no words for says nothing, and void or written off says nothing is
 *   owed. A paid invoice says how the saved method paid it only when ops says it charged it, in the
 *   seam's words (`chargedWords`, section 5 at 0b51e49), with that a receipt was emailed only when
 *   ops says so. While ops says a bank payment on it clears,
 *   the seam's sentence says so and nothing is offered.
 * - "HOW DID WE DO?" ABOVE EVERY WAY TO PAY (l.726-729), on every invoice the page shows, as the
 *   seam asks it, sent as `invoice.feedback`: Very good on the tap, and Not good, Mixed and Good
 *   with Send, once the page has asked which area and what was missed, both hers to leave, with the
 *   business's promise to clean it again, as Feedback asks them of a clean, by the one rule both
 *   pages ask by (`asksWhatWasMissed`, `lib/faces.ts`, slice V4). The owner decided on 2026-10-16
 *   that any answer naming a missed area starts a re-clean, Good included (ops #72), and the replay
 *   of the walk found this page sending Good on the tap, so it could never name one. Once it is
 *   sent the page thanks her in the seam's words, and after a face below Good (`belowGood`, ops's
 *   own line, the seam's sections 4 and 5, which Good with an area does not move) the ways to pay
 *   fold away behind "Ways to pay this invoice", which opens them again. The face lit is the one
 *   she has just sent; else her newest answer about the clean, given here or on Home's card for the
 *   clean, which ops counts on the invoice, while ops could not have had it when it last pushed the
 *   invoice, which lags it: it waits to be sent, or ops's receipt of it came after that push (the
 *   Worker's `afterPush`, provisional P47, the owner's walk of 2026-10-16, where the page lit an
 *   older face and she answered again); else the one ops says counts. So a reload keeps them folded
 *   while her face below Good, or ops's, counts. Ops records nothing past twelve answers from home
 *   on an invoice, and says so on its receipt, which the Worker gives as her newest answer's
 *   `recorded`; then the page says ops already has her answers, with the phone, and offers no
 *   faces, and what counts is what ops said.
 * - WHAT THE REPLAY OF THE OWNER'S WALK FOUND IN IT (round four, slice F1): her line, under Other or
 *   with no area chosen, sends the area "Other" with it, so ops opens the re-clean the page promised
 *   (`areaSent`, ops's section 4 at 9f10a8b and section 11.6, question 7), and Other with nothing
 *   written names none; ops then holds the saved method's charge after Good with an area, and pushes
 *   no area, so after Good the page says nothing of the charge and the plain thanks stand
 *   (`chargeReply`, `chargeFollows`); a tap on a face that asks
 *   more brings "Which area?" down to Send into view, clear of the nav (`bringIntoView`); a face
 *   tapped is drawn chosen, a ring and no fill, and described as not sent, until Send fills it; and
 *   the re-clean promise is drawn only on the clean's day and the day after (`recleanPromised`).
 * - WHAT HAPPENS NEXT (round six, slice S3, P86): after an answer sent from this page that named
 *   what was missed, under the thanks, "The office will be in touch to put it right."
 *   (`FEEDBACK.inTouch`), home's own line beside ops's words and never in place of them, read out
 *   with the thanks; not where ops's words for a lesser face already say it, never that a clean is
 *   booked, and not on a page read afresh, which knows only the face ops says counts. The face
 *   looks are shared with Feedback (`components/faceLooks.ts`).
 * - ONE REQUEST HOWEVER OFTEN IT IS TAPPED, as on Feedback: the key is minted once for the answer it
 *   was minted for, so a retry after a failure names the same request and the door writes one row,
 *   and a changed answer is a new key. While one is out, another tap does nothing. After one is
 *   sent the invoice is read again, so what ops has said of it since is drawn.
 * - WHAT THE DOOR ANSWERS: sent is thanked; a 401 is the way to the door; a 404 reads the invoice
 *   again, and an invoice no longer hers goes to Payments; a line that looks like a code is marked,
 *   kept and given the focus, in the seam's words; anything else says it did not go through, with
 *   the phone, and keeps her answer to try again.
 * - THE WAYS TO PAY, one screen at a time in the owner's order (ops #46), held in the page and never
 *   in its address: Zelle first (`components/Zelle.tsx`), which needs no request up; then, only
 *   while ops says the bank and card screens exist (`pay.online`), "Pay another way" to the bank,
 *   and "I can't pay by bank" to the card (`components/StripeWay.tsx`), each with the way back.
 *   Each move gives the focus to the screen moved to, and ops saying the other screens are gone
 *   brings her back to Zelle. Without a Zelle tag from ops they start at the bank, and with neither,
 *   the office's number. Opened by "Back to your invoice" after Stripe's back arrow, they start on
 *   the way she left from, the bank or the card, which the link hands the page in the history and
 *   never in the address (slice V3, after the walk was replayed, whose way back opened on Zelle).
 *   Loaded afresh because ops said the words beside her tick had changed (`409 terms_changed`,
 *   ops's section 12.3, round five, slice A4), they start on the way she pressed on, which says so.
 * - AN INVOICE THE SAVED METHOD PAYS offers none of them (l.730-740): in their place, what the saved
 *   method will do and "I'm paying by Zelle" (`components/SavedMethodPays.tsx`), and "How did we
 *   do?" says what her face means for the charge (`chargeReply`). Where ops says the saved method
 *   does not pay it after all, as a refusal of "I'm paying by Zelle" (`409
 *   not_charged_automatically`), the page says so above the ways to pay, in the refusal's own words
 *   (`PAUSE_REFUSAL_WORDS`), and draws them: Zelle alone,
 *   or the office's number, while the copy still holds the saved method, since the door refuses the
 *   bank and the card from the copy until ops's push catches up. Where ops has said the method no
 *   longer charges at all (`methodStopped`: its receipt of her Stop, or the household's push that
 *   nothing is saved), the page promises no charge, as Payments then says none, and draws the same
 *   ways to pay; but not where ops last said the charge is at Stripe (`chargeClaimed`), which a
 *   Stop does not cancel, so the page goes on saying it is being charged now.
 * - WHAT OPS REFUSES A PRESS changes what is offered, until its push says how the invoice stands:
 *   Stripe off leaves Zelle alone, a bank payment clearing, nothing owed and the saved method paying
 *   it each say so in place of every way to pay, and every page made puts the office's number in
 *   place of Pay on the bank and the card. Each reads the invoice again. The Worker carries ops's
 *   refusal of her newest ask until the push catches up with it (`payAsk`), so a page drawn afresh,
 *   after a reload or with the ways to pay folded and opened again, holds to it too. Where the read
 *   after a refusal takes the ways to pay away, the focus goes to what now says how the invoice
 *   stands: the card's state, or what the saved method will do.
 * - BACK FROM PAYING ON STRIPE'S PAGE in any tab (`lib/paying.ts`), the card says the payment is on
 *   its way right under the amount, in place of the day it is due, every other fact still drawn, and
 *   no way to pay is offered, until ops's push says how the invoice stands: the amount is still
 *   ops's balance, and home marks nothing paid. A page already open in another tab draws the mark
 *   at once and reads the invoice again (`components/Guarded.tsx`).
 * - "I SENT IT BY ZELLE" (slice V2; reported to ops since round five's slice B2, ops's section 11.7,
 *   P53), in the card with Zelle's tag, wherever Zelle's screen is drawn: among the ways to pay, and
 *   while she pays it by Zelle in place of her saved method. It opens a step naming what she was
 *   asked to send and the day she sent it, from the days the contract allows (`zelleSentDays`);
 *   "Yes, I sent it" sends `invoice.zelle_sent` through the request door under a key minted for the
 *   press, kept only while the door could not be reached. The Worker holds the invoice on its way at
 *   once, home's own bridge, and ops's answer from then on: the card names the day she picked in
 *   place of the day it is due (`onItsWayLine`), drawn at once from the door taking it and taking the
 *   focus, and no way to pay is offered. While ops has not answered, the page reads the invoice
 *   again every three seconds, six times at most, so a refusal is said soon, where the link was, in
 *   ops's words (`zelleReportRefusal`), with the focus. Under "How did we do?", for her own report,
 *   what she told the office she sent stays as a record, the tag and the memo with no Copy and no
 *   amount, since ops holds only her day and its balance moves under her report (round six, slice
 *   S1) (`components/ZelleSent.tsx`); another client's report is said on the card without saying
 *   who, with the office's number for one not sent after all. The page tells her other tabs, which
 *   read theirs (`tellZelleWord`). A press that does not go through says so in the step, with the
 *   phone, and keeps it to try again.
 * - "I HAVE NOT SENT IT YET" (ops's section 11.7, "Taking it back", P53), under the record of her own
 *   report once ops holds it, for the client who made it alone: `invoice.zelle_withdrawn` through
 *   the request door under a key minted for the press, kept only while the door could not be
 *   reached. In its place at once, with the focus, "Telling the office you have not sent it yet.";
 *   the Worker ends home's own mark at once, and the page reads the invoice again every three
 *   seconds, six times at most, while ops has not answered. Where nothing holds the invoice any
 *   more, every way to pay comes back, with a line above them that she took it back, or what ops
 *   refused it with where no report stands, which takes the focus where it came of her press; but
 *   ops's `invoice_not_open` says the invoice owes nothing now, so its words stand in place of every
 *   way to pay, and of what the saved method will do, until the push says how the invoice stands
 *   (`owesNothingSaid`), as where ops refuses a Pay so. Where a report still stands,
 *   `reported_by_another` among them, what came of it is said in place of the link. The page tells
 *   her other tabs.
 * - HOW AND WHEN SHE PAID (ops's section 11.2, round five's slice B3, P62): right under the card,
 *   "Payments on this invoice", each payment ops recorded in the Payments artboard's form for one
 *   (`paymentSaid`), "By Zelle, 16 October. Receipt emailed.", with the amount applied to this
 *   invoice, a refund's below nothing; a refund as refunded and a correction as the office's
 *   adjustment. Ops's record that money arrived, which never reads the invoice paid: the card says
 *   what ops says of its state. No part where ops sent none.
 * - ITS DOCUMENTS (section 11.2, P62), at the foot of the page, below every way to pay
 *   (`components/Documents.tsx`): the invoice's PDF, each receipt's, and on a voided invoice its
 *   cancellation's, each a button in ops's words while ops says it is made, and ops's line while one
 *   is being made. A press asks ops for a signed link through the request door, and the same tab goes
 *   there.
 * - A PERSON SHOWN NO MONEY, an invoice of another household, and one that does not exist are all
 *   the Worker's 404, which goes to Payments, whose own read says where she stands.
 * - AN INVOICE NOT HERE YET (ops's plan for clients reaching only home, section 7, ask 5): while her
 *   first fill is still arriving, the Worker's 404 for an id it does not hold says it may still be
 *   on its way, of any such id alike, and the page says "This invoice is on its way to your page."
 *   and reads itself again until it comes, or the Worker no longer says so (`NotHereYet`).
 * - AN INVOICE BEHIND A DOOR THAT WAITS FOR HER CODE (the review of 7e33d61, finding 1): where the
 *   Worker says too that a client's door ops pushed for the address she signed in with waits for her
 *   code, as one riding a household's first invoice email does, the page asks for a fresh code to
 *   that address, which binds the door and tells ops she accepted, and then reads the invoice as on
 *   its way (`CodeOpens`).
 */
import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type Ref } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  getInvoice,
  reportZelleSent,
  sendRequest,
  takeBackZelle,
  type InvoicePage,
  type InvoicePlace,
  type PagePay,
  type PayAsk,
  type SignedInWith,
  type TooMany,
  type ZelleReport,
} from '../api.ts';
import { GHOST_BUTTON, NAVY_BUTTON } from '../components/buttons.ts';
import { documentOnArrival, Documents } from '../components/Documents.tsx';
import { FACE_CHOSEN, FACE_FREE, FACE_SENT } from '../components/faceLooks.ts';
import { FaceIcon } from '../components/FaceIcon.tsx';
import { Folding } from '../components/Folding.tsx';
import { FreshCode } from '../components/FreshCode.tsx';
import { Guarded, type Signed } from '../components/Guarded.tsx';
import { Phone, WithPhone } from '../components/Phone.tsx';
import { ReturnWords } from '../components/ReturnWords.tsx';
import { useHeadingFocus } from '../components/RouteFocus.tsx';
import {
  chargeClaimed,
  chargeReply,
  notChargedSaid,
  PAUSE_POLL_GAP_MS,
  PAUSE_POLLS,
  SavedMethodPays,
} from '../components/SavedMethodPays.tsx';
import { Shell, useOnItsWay } from '../components/Shell.tsx';
import { ENDINGS, StripeWay, type Ending } from '../components/StripeWay.tsx';
import { ZelleWay, type ReportOffer } from '../components/Zelle.tsx';
import { ZelleSent, type TakeBackOffer } from '../components/ZelleSent.tsx';
import { dayInWords } from '../lib/dates.ts';
import { areaSent, asksWhatWasMissed, belowGood, recleanPromised } from '../lib/faces.ts';
import {
  daysOf,
  invoiceHeading,
  onItsWayLine,
  owedStateOf,
  owesNothingSaid,
  payableHere,
  zelleSentDays,
} from '../lib/invoices.ts';
import {
  bringIntoView,
  bringScreenToTop,
  bringToTop,
  bringToTopAndHold,
  CLEAR_OF_THE_NAV,
} from '../lib/motion.ts';
import {
  cameToPay,
  forget,
  forgetTermsChanged,
  noteReturnTo,
  opensOn,
  readReturn,
  tellZelleWord,
  termsChangedOn,
  wayAsked,
  type Back,
  type WayToPay,
} from '../lib/paying.ts';
import { useReadAgain } from '../lib/readAgain.ts';
import { homeNameOf } from '../lib/visits.ts';
import { DOCUMENT_QUERY, PAYMENTS_PATH } from '../paths.ts';
import {
  AREAS,
  chargedWords,
  dollars,
  FACES,
  FAILED_BEFORE_PHONE,
  FEEDBACK,
  HOLDING,
  INVOICE,
  INVOICE_FEEDBACK,
  invoiceNumbered,
  issuedOnDay,
  LOOKS_LIKE_A_CODE,
  PAUSE_REFUSAL_WORDS,
  paymentSaid,
  PAYING,
  PAYMENTS,
  SAVED_METHOD_PAYS,
  tooManyBeforePhone,
  UPDATES_BY_ITSELF,
  ZELLE,
  zelleReportRefusal,
  zelleTakeBackRefusal,
  type Face,
} from '../words.ts';
import { FEEDBACK_LINE_MAX } from './Feedback.tsx';
import { HOLDING_BOUND_MS, HOLDING_CAP_MS } from './Holding.tsx';

const EYEBROW = 'font-mono text-small tracking-widest text-text-emphasis uppercase';
const QUESTION = 'text-body font-semibold text-text-body';

/** The focus ring, drawn on an area's chip while its hidden radio button has it, as on Feedback. */
const RING =
  'has-focus-visible:outline-3 has-focus-visible:outline-offset-2 has-focus-visible:outline-focus-ring';

const CHIP =
  'flex min-h-tap cursor-pointer items-center gap-1.5 rounded-control border-2 px-4 text-small';
const CHIP_CHOSEN = 'border-surface-brand bg-surface-brand-soft font-semibold text-text-body';
const CHIP_FREE =
  'border-border-strong bg-surface-raised text-text-body hover:bg-surface-accent-soft';

/** Ops's states in which the invoice is owed, whose amount is what is still to pay. */
const OWED: ReadonlySet<string> = new Set(['sent', 'part_paid', 'overdue']);

/** Ops's states in which nothing is owed and nothing was paid. */
const NOTHING_OWED: ReadonlySet<string> = new Set(['void', 'written_off']);

/** A face word ops sent, or null for one this build does not know. */
const faceOf = (word: string | undefined): Face | null =>
  FACES.find(({ face }) => face === word)?.face ?? null;

export function Invoice(signed: Signed) {
  const { invoiceId = '' } = useParams();
  const load = useCallback(() => getInvoice(invoiceId), [invoiceId]);
  /** The invoice this page said was on its way, so that it takes the focus once it comes. */
  const [waitedFor, setWaitedFor] = useState<string | null>(null);
  const waited = useCallback(() => setWaitedFor(invoiceId), [invoiceId]);
  /** The invoice whose page took her code, so that the line saying it is on its way takes the focus. */
  const [codedFor, setCodedFor] = useState<string | null>(null);
  const { pathname, search, hash, state: entry } = useLocation();
  /** What the address back from Stripe's page says, where one returned her here (P110), or none. */
  const back = readReturn(search);
  // Back paid, in whichever tab: noted once, as the page is opened and before the invoice is read,
  // as the return page notes it in the tab that left to pay this very invoice and as P35's mark in
  // any other, so the place's first read already holds its ways to pay as on its way
  // (`noteReturnTo`). Noted once for this entry in the history, which keeps that it was, at the
  // same address: a reload, Back or Forward to it, or a tab her browser restores, opens it again
  // with the address back still on it, and must mark nothing anew once ops's push has let the mark
  // go (the review of 3efb79f).
  const [noted] = useState(() => noteReturnTo(invoiceId, back, entry));
  const navigate = useNavigate();
  useEffect(() => {
    if (noted !== null) void navigate({ pathname, search, hash }, { replace: true, state: noted });
    // Once, as the page is first drawn.
  }, []);
  return (
    // Keyed by the invoice, so another invoice's page is another read.
    <Guarded
      key={invoiceId}
      load={load}
      signed={signed}
      at="payments"
      heading={INVOICE.eyebrow}
      notFound={<Navigate to={PAYMENTS_PATH} replace />}
      failed={back === null ? undefined : <NotRead back={back} />}
      arriving={(reload, invited) =>
        invited ? (
          <CodeOpens
            signedInWith={signed.signedInWith}
            reload={reload}
            onProved={() => setCodedFor(invoiceId)}
            back={back}
          />
        ) : (
          <NotHereYet
            reload={reload}
            onDrawn={waited}
            afterCode={codedFor === invoiceId}
            back={back}
          />
        )
      }
    >
      {(place, reload) => (
        <InvoiceDrawn
          place={place}
          signed={signed}
          reload={reload}
          came={waitedFor === invoiceId}
          back={back}
        />
      )}
    </Guarded>
  );
}

/**
 * THE WORDS BACK FROM STRIPE'S PAGE, SIGNED IN (ops's `docs/seams/home.md` section 5; ops's plan for
 * clients reaching only home, section 7, ask 3; `docs/seam.md` P110). The office's page, and a page
 * her texted link made, come back to this page with the address back on it, and she may be signed
 * in, or sign in after coming back with the address kept (P107). So at the top of the page, as the
 * page Stripe sends her back to draws them above Payments (`PaymentReturn.tsx`), the same words by
 * the same mapping (`components/ReturnWords.tsx`): "Payments" over the heading, the page's `h1`, and
 * the callout. After `not-paid` its line on how to pay is home's own only above the invoice drawn
 * with a way to pay it (`invoiceBelow`), and says to choose a way to pay below
 * (`INVOICE_HOW_TO_PAY`), not section 11.4's line for Payments, since the ways to pay draw no
 * button named Pay (the fifth client-journey walk's review, finding 2); above a page that draws no
 * invoice and no way to pay (on its way to her page, behind a door that waits for her code, or not
 * read), and above an invoice that offers no way to pay it (paid, nothing owed, held on its way, or
 * only the office's number), it is section 5's own, as on the door, since 11.4 gives home's line
 * only where "the Pay button is below it" (the review of fe216b8, finding 1). The words note
 * nothing themselves: what the page notes of a payment, for this invoice alone and from any tab, is
 * `noteReturnTo`'s, which holds its ways to pay as on its way, and the invoice below is never drawn
 * paid till ops pushes it (CLAUDE.md binding rule 1). Nothing for an address back the contract does
 * not define.
 */
function WordsBack({
  back,
  invoiceBelow = false,
}: {
  readonly back: Back | null;
  /**
   * The invoice is drawn under the words with a way to pay it: only `InvoiceDrawn` says so, and only
   * where it draws one.
   */
  readonly invoiceBelow?: boolean;
}) {
  if (back === null) return null;
  return (
    <div data-returned className="flex flex-col gap-5">
      <ReturnWords back={back} below={invoiceBelow ? 'ways' : 'nothing'} />
    </div>
  );
}

/**
 * The page's own heading, "Your invoice" or the invoice's day: the page's `h1`, or an `h2` under the
 * words back from Stripe's page, whose heading is then the page's, as on the door (`Door.tsx`).
 */
const titleTag = (back: Back | null) => (back === null ? 'h1' : 'h2');

/**
 * An invoice that cannot be read, opened with an address back: the words back first, which need no
 * read, so a failed read never reads as a failed payment, as on the return page; then "Your invoice"
 * and the line that says it did not go through, with the phone, as on any failed read.
 */
function NotRead({ back }: { readonly back: Back }) {
  return (
    <Shell at="payments">
      <WordsBack back={back} />
      <div>
        <h2 className="font-serif text-h2 font-semibold text-pretty text-text-body">
          {INVOICE.eyebrow}
        </h2>
        <p className="mt-2 text-body text-pretty">
          {FAILED_BEFORE_PHONE} <Phone after="." />
        </p>
      </div>
    </Shell>
  );
}

/**
 * AN INVOICE NOT HERE YET (ops's plan for clients reaching only home, section 7, ask 5; home's
 * answer of 2026-10-20, item 5). The Worker says an id it does not hold may still be on its way
 * while her first fill is still arriving, by the rule Home reads it by, and says it of every such
 * id, whosever it is. Drawn in the shell at Payments, as a failed read of this page is: "Your
 * invoice" as the heading, and straight under it, as on the waiting page (`Holding.tsx`), a ring
 * that turns where the device allows motion, beside a status a screen reader is told of, that the
 * invoice is on its way to her page and that the page updates by itself. Nothing of money and
 * nothing of the id. It reads itself again as the waiting page does (`lib/readAgain.ts`), and the
 * invoice is drawn in its place once it comes, with the focus on its heading (`came`); plain not
 * found goes to Payments, as for any invoice not hers. After an hour in view it stops and says so,
 * and "Check again" reads once more and starts over, handing the focus to the status.
 */
function NotHereYet({
  reload,
  onDrawn,
  afterCode = false,
  back = null,
}: {
  readonly reload: () => void;
  readonly onDrawn: () => void;
  /** Drawn once her code opened the page, in place of its code step: the status takes the focus. */
  readonly afterCode?: boolean;
  /** What the address back from Stripe's page says, said above it (`WordsBack`), or none. */
  readonly back?: Back | null;
}) {
  const { stopped, again } = useReadAgain(reload, {
    on: true,
    capMs: HOLDING_CAP_MS,
    boundMs: HOLDING_BOUND_MS,
  });
  const status = useRef<HTMLDivElement>(null);
  useEffect(onDrawn, [onDrawn]);
  // The code step it replaces had the focus, and has gone: the status says what came of the code.
  useEffect(() => {
    if (afterCode) status.current?.focus();
  }, []);
  const Title = titleTag(back);
  return (
    <Shell at="payments">
      <WordsBack back={back} />
      <div>
        <Title className="font-serif text-h2 font-semibold text-pretty text-text-body">
          {INVOICE.eyebrow}
        </Title>
        <div
          data-progress
          className="mt-4 flex items-start gap-4 rounded-card border border-border-subtle bg-surface-raised p-5"
        >
          {stopped ? null : (
            <span
              data-working
              aria-hidden="true"
              className="mt-1 block size-6 shrink-0 rounded-full border-[3px] border-surface-brand-soft border-t-surface-brand motion-safe:animate-turn"
            />
          )}
          <div ref={status} role="status" tabIndex={-1}>
            <p className="text-body text-pretty text-text-body">{INVOICE.notHereYet}</p>
            <p className="mt-2 text-body text-pretty text-text-body">
              {stopped ? HOLDING.stopped : UPDATES_BY_ITSELF}
            </p>
          </div>
        </div>
        {stopped ? (
          <button
            type="button"
            onClick={() => {
              again();
              status.current?.focus();
            }}
            className={`mt-4 ${GHOST_BUTTON}`}
          >
            {HOLDING.checkAgain}
          </button>
        ) : null}
      </div>
    </Shell>
  );
}

/**
 * AN INVOICE BEHIND A DOOR THAT WAITS FOR HER CODE (the review of 7e33d61, finding 1). Ops writes a
 * client's door on another household `invited` and quiet, riding that household's first invoice
 * email (ops #76, ask 1; P108), and only a code binds a door and tells ops she accepted, so a session
 * she already holds sees nothing of it. Where none of her households holds the id and such a door
 * waits for the address or number she signed in with, the Worker says so (`invited`), and the page
 * asks for a fresh code to it, as the step-up sheets do (`FreshCode`), emailed to an address and
 * texted to a number, in the shell at Payments under "Your invoice", in the card the page says an
 * invoice on its way in. Once the code works it reads the invoice again, which then says it is on
 * its way until ops grants the door and its fill brings the invoice (`NotHereYet`, with the focus).
 * It never reads itself again while it waits for the code, which nothing else would change; nothing
 * of money and nothing of the id. One signed in by a number where no code can be texted is told to
 * call, as in every code step.
 */
function CodeOpens({
  signedInWith,
  reload,
  onProved,
  back = null,
}: {
  readonly signedInWith: SignedInWith;
  readonly reload: () => void;
  readonly onProved: () => void;
  /** What the address back from Stripe's page says, said above it (`WordsBack`), or none. */
  readonly back?: Back | null;
}) {
  const Title = titleTag(back);
  return (
    <Shell at="payments">
      <WordsBack back={back} />
      <div>
        <Title className="font-serif text-h2 font-semibold text-pretty text-text-body">
          {INVOICE.eyebrow}
        </Title>
        <div className="mt-4 rounded-card border border-border-subtle bg-surface-raised p-5">
          <FreshCode
            signedInWith={signedInWith}
            why={INVOICE.codeFirst}
            submit={INVOICE.codeContinue}
            closedBeforePhone={INVOICE.codeCannotBeforePhone}
            onProved={async () => {
              onProved();
              reload();
              return null;
            }}
          />
        </div>
      </div>
    </Shell>
  );
}

/** The answer this page sent, once the door had it: its request's id and its face. */
interface Sent {
  readonly id: string;
  readonly face: Face;
}

interface DrawnProps {
  readonly place: InvoicePlace;
  readonly signed: Signed;
  readonly reload: () => void;
  /** Drawn in place of the page saying it was on its way, so its heading takes the focus. */
  readonly came?: boolean;
  /** What the address back from Stripe's page says, said at the top (`WordsBack`), or none. */
  readonly back?: Back | null;
}

function InvoiceDrawn({ place, signed, reload, came = false, back = null }: DrawnProps) {
  const title = useRef<HTMLHeadingElement>(null);
  // The invoice came while she watched it said on its way: its heading takes the focus, as Home's
  // does when the household comes while she watches the waiting page. Its own heading, the title,
  // which is the page's `h1` only with no words back from Stripe's page above it; under them it is
  // an `h2`, and their heading, which she heard while it was on its way, says nothing of its coming
  // (the review of fe216b8, finding 3).
  useHeadingFocus(came, title);
  const { invoice } = place;
  const heading = invoiceHeading(invoice);
  const home = homeNameOf(invoice, place.homes);
  /** The invoice drawn when the door said the saved method does not pay it, before a read since. */
  const [notChargedOn, setNotChargedOn] = useState<InvoicePage | null>(null);
  /** The door said so here, so the line that says it takes the focus. */
  const [toldHere, setToldHere] = useState(false);
  const notChargedLine = useRef<HTMLParagraphElement>(null);
  /** She pressed "I'm paying by Zelle" here, so what ops answered it with takes the focus. */
  const pausedHere = useRef(false);
  /** The invoice drawn when ops refused a press on the ways to pay, before a read since. */
  const endedOn = useRef<InvoicePage | null>(null);
  /** The invoice drawn now, which a refusal is about. */
  const drawn = useRef(invoice);
  drawn.current = invoice;
  const standing = useRef<HTMLDivElement>(null);
  const savedBlock = useRef<HTMLDivElement>(null);
  // Ops has said the saved method no longer charges, by its receipt of her Stop or the household's
  // push, and the invoice's own push has not caught up, as the Worker reads it; but a Stop leaves a
  // charge at Stripe as it stands, which the page goes on saying.
  const stopped = invoice.methodStopped === true && !chargeClaimed(invoice);
  // The saved method pays it, unless ops has said it does not: on her newest "I'm paying by Zelle",
  // which the Worker carries until the push catches up, or at the door, until the invoice is read;
  // or that the method no longer charges at all.
  const savedPays =
    invoice.autopay !== undefined &&
    !stopped &&
    !notChargedSaid(invoice) &&
    notChargedOn !== invoice;
  const payable = payableHere(savedPays ? invoice : { ...invoice, autopay: undefined });
  // Only ops's word says the saved method does not pay it, and the copy still holds the method,
  // which the door reads, so it refuses the bank and the card until the push catches up: Zelle alone
  // is offered, or the office's number without it.
  const pay =
    (notChargedSaid(invoice) || stopped) && invoice.autopay !== undefined
      ? { ...invoice.pay, online: false }
      : invoice.pay;
  // Somebody on the household reported sending it by Zelle and the Worker holds it on its way
  // (section 11.7, P53), drawn at once from the door taking her report here until the invoice is read
  // again; or she came back from paying it on Stripe's page in this tab or another of her browser's,
  // as the place draws it (P35); and ops has not yet said otherwise.
  const onItsWay = useOnItsWay();
  /** The invoice drawn when the door took her report here, with what it holds, before a read since. */
  const [saidOn, setSaidOn] = useState<{ on: InvoicePage; report: ZelleReport } | null>(null);
  const report = saidOn?.on === invoice ? saidOn.report : invoice.zelleReport;
  const reportHeld = report?.state === 'on_its_way';
  /** She took her own report back, and nothing holds the invoice on its way (11.7). */
  const takenBack = report?.state === 'taken_back' ? report : null;
  /** What came of her taking it back while a report still holds the invoice. */
  const takeBackNow = reportHeld ? report.takeBack : undefined;
  const onItsWayNow = reportHeld
    ? onItsWayLine({ ...invoice, zelleReport: report }, false)
    : payable === null
      ? null
      : onItsWayLine(
          { ...invoice, autopay: undefined, zelleReport: undefined },
          onItsWay(invoice.id),
        );
  const held = onItsWayNow !== null;
  /** She reported it here, so what now says it is on its way takes the focus. */
  const sentHere = useRef(false);
  useEffect(() => {
    if (!held || !sentHere.current) return;
    sentHere.current = false;
    standing.current?.focus();
  }, [held]);
  /** Her report is on its way to the request door. */
  const [saying, setSaying] = useState(false);
  const askingNow = useRef(false);
  /** The key minted for her press, kept only while the door could not be reached. */
  const minted = useRef<string | null>(null);
  /** What did not go through of her report, said in the step. */
  const [sayProblem, setSayProblem] = useState<WordProblem | null>(null);
  /** Past the door's own limits: whose count and when, as the door said them (slice S1). */
  const [sayLimit, setSayLimit] = useState<TooMany>();
  /** What the door refused her report here with, ops's code, until she presses again. */
  const [refusedHere, setRefusedHere] = useState<{ readonly code?: string } | null>(null);
  const refusal = refusedHere ?? (report?.state === 'refused' ? { code: report.code } : null);
  /** She reported it here, so a refusal that comes of it takes the focus. */
  const reportedHere = useRef(false);
  const refusalLine = useRef<HTMLParagraphElement>(null);
  const refusedNow = refusal !== null;
  useEffect(() => {
    if (!refusedNow || !reportedHere.current) return;
    reportedHere.current = false;
    refusalLine.current?.focus();
  }, [refusedNow]);

  // Her own report not yet answered by ops, or her taking it back: the invoice is read again every
  // three seconds, six times at most, so what ops answers is drawn without her reloading.
  const polls = useRef(0);
  const waitingOnOps =
    (reportHeld &&
      ((report.sending === true && report.own === true) || takeBackNow?.state === 'sending')) ||
    takenBack?.sending === true;
  useEffect(() => {
    if (!waitingOnOps || polls.current >= PAUSE_POLLS) return;
    const timer = setTimeout(() => {
      polls.current += 1;
      reload();
    }, PAUSE_POLL_GAP_MS);
    return () => clearTimeout(timer);
  }, [invoice, waitingOnOps, reload]);

  /**
   * Reports that she sent it by Zelle on `sentOn` (section 11.7), through the request door, and
   * reads the invoice again.
   */
  async function reportIt(sentOn: string) {
    if (askingNow.current) return;
    askingNow.current = true;
    setSaying(true);
    setSayProblem(null);
    setRefusedHere(null);
    minted.current ??= crypto.randomUUID();
    const answer = await reportZelleSent(minted.current, place.household.id, invoice.id, sentOn);
    askingNow.current = false;
    setSaying(false);
    if (answer.outcome !== 'failed') minted.current = null;
    switch (answer.outcome) {
      case 'sent':
        sentHere.current = true;
        reportedHere.current = true;
        polls.current = 0;
        setSaidOn({
          on: invoice,
          report: { state: 'on_its_way', sentOn, own: true, sending: true },
        });
        tellZelleWord();
        reload();
        return;
      case 'signed_out':
        signed.onSignedOut();
        return;
      // The invoice is gone, or not hers to act on now, which the door answers alike (binding rule
      // 2): said in the step, never silent, and the invoice read again, which goes to Payments once
      // it is not hers.
      case 'not_found':
        setSayProblem('not_here');
        reload();
        return;
      case 'refused':
        // Said where the link was, in ops's words; the read again may take the ways to pay away,
        // and then what says how the invoice stands takes the focus.
        reportedHere.current = true;
        endedOn.current = drawn.current;
        setRefusedHere({ code: answer.code });
        reload();
        return;
      case 'too_many':
        setSayLimit(answer.limit);
        setSayProblem('too_many');
        return;
      default:
        setSayProblem(answer.outcome === 'failed' && answer.offline ? 'offline' : 'failed');
    }
  }

  /** Her taking it back is on its way to the request door. */
  const [backing, setBacking] = useState(false);
  const backingNow = useRef(false);
  /** The key minted for her press, kept only while the door could not be reached. */
  const backMinted = useRef<string | null>(null);
  /** What did not go through of her taking it back, said under the link. */
  const [backProblem, setBackProblem] = useState<WordProblem | null>(null);
  const [backLimit, setBackLimit] = useState<TooMany>();
  /** What the door refused her taking it back with, until she presses again. */
  const [backRefusedHere, setBackRefusedHere] = useState<{ readonly code?: string } | null>(null);
  /** She pressed it here, so what comes of it takes the focus. */
  const backHere = useRef(false);
  /** The line in place of the link, and the line above the ways to pay. */
  const takeBackLine = useRef<HTMLParagraphElement>(null);
  const takenBackLine = useRef<HTMLParagraphElement>(null);

  /**
   * Takes her report back (ops's section 11.7, "Taking it back"), through the request door, and
   * reads the invoice again.
   */
  async function takeItBack() {
    if (backingNow.current) return;
    backingNow.current = true;
    setBacking(true);
    setBackProblem(null);
    setBackRefusedHere(null);
    backMinted.current ??= crypto.randomUUID();
    const answer = await takeBackZelle(backMinted.current, place.household.id, invoice.id);
    backingNow.current = false;
    setBacking(false);
    if (answer.outcome !== 'failed') backMinted.current = null;
    switch (answer.outcome) {
      case 'sent':
        backHere.current = true;
        polls.current = 0;
        if (report?.state === 'on_its_way') {
          setSaidOn({ on: invoice, report: { ...report, takeBack: { state: 'sending' } } });
        }
        tellZelleWord();
        reload();
        return;
      case 'signed_out':
        signed.onSignedOut();
        return;
      // The same, said under the link, never silent.
      case 'not_found':
        setBackProblem('not_here');
        reload();
        return;
      case 'refused':
        backHere.current = true;
        setBackRefusedHere({ code: answer.code });
        reload();
        return;
      case 'too_many':
        setBackLimit(answer.limit);
        setBackProblem('too_many');
        return;
      default:
        setBackProblem(answer.outcome === 'failed' && answer.offline ? 'offline' : 'failed');
    }
  }

  // What came of her taking it back while a report may still stand: the door's refusal here, or
  // ops's, or home's giving up on it, said in place of the link.
  const backSaid =
    backRefusedHere !== null ? (
      <TakeBackRefusalSaid code={backRefusedHere.code} />
    ) : takeBackNow?.state === 'refused' ? (
      <TakeBackRefusalSaid code={takeBackNow.code} />
    ) : takeBackNow?.state === 'failed' ? (
      <TakeBackRefusalSaid code={undefined} />
    ) : undefined;
  const takeBack: TakeBackOffer = {
    offered:
      reportHeld && report.own === true && report.sending !== true && takeBackNow === undefined,
    onPress: () => void takeItBack(),
    pressing: backing,
    sending: takeBackNow?.state === 'sending',
    said: backSaid,
    problem:
      backProblem === null ? undefined : <ProblemSaid problem={backProblem} limit={backLimit} />,
    lineRef: takeBackLine,
  };
  /**
   * Above the ways to pay, once nothing holds the invoice: what came of her taking it back; or, in
   * place of them, ops's word that the invoice owes nothing now (`owesNothingSaid`).
   */
  const takenBackSaid =
    takenBack === null ? null : takenBack.sending === true ? (
      ZELLE.takingBack
    ) : takenBack.code !== undefined ? (
      <TakeBackRefusalSaid code={takenBack.code} />
    ) : (
      ZELLE.takenBack
    );
  // Where it came of her press, what it came to takes the focus: the line in place of the link, or
  // the one above the ways to pay; once more as it changes while ops has not answered, then no more.
  const backShown =
    takenBack !== null
      ? { where: 'above', waiting: takenBack.sending === true, key: JSON.stringify(takenBack) }
      : reportHeld && (takeBack.sending || backSaid !== undefined)
        ? {
            where: 'record',
            waiting: takeBack.sending,
            key: JSON.stringify([takeBackNow, backRefusedHere, report.own]),
          }
        : null;
  const backKey = backShown === null ? null : `${backShown.where}:${backShown.key}`;
  useEffect(() => {
    if (!backHere.current || backShown === null) return;
    (backShown.where === 'above' ? takenBackLine : takeBackLine).current?.focus();
    if (!backShown.waiting) backHere.current = false;
    // `backKey` names what is shown, and where; the rest follows from it.
  }, [backKey]);

  /** "I sent it by Zelle", wherever Zelle's screen is drawn on this page. */
  const reportOffer: ReportOffer = {
    days: zelleSentDays(invoice, place.today),
    today: place.today,
    onReport: (day) => void reportIt(day),
    sending: saying,
    problem:
      sayProblem !== null ? (
        <ProblemSaid problem={sayProblem} limit={sayLimit} />
      ) : report?.state === 'failed' ? (
        <ProblemSaid problem="failed" />
      ) : undefined,
    refusal: refusal === null ? undefined : <RefusalSaid code={refusal.code} />,
    refusalRef: refusalLine,
  };
  /**
   * What she told the office she sent, under her own report on its way, with her way to take it
   * back; under another person's, the office's number: never the balance, which moves under her
   * report while ops holds only her day (`components/ZelleSent.tsx`).
   */
  const whatSheSent = reportHeld ? (
    <ZelleSent zelle={invoice.pay.zelle} own={report.own === true} takeBack={takeBack} />
  ) : null;
  // Said above the ways to pay, so only where they are drawn.
  const notCharged =
    !savedPays && (toldHere || notChargedSaid(invoice)) && payable !== null && !held;
  // Ops's push has said how it stands, so it is no longer on its way in any tab, and a push that
  // offers it again offers the ways to pay.
  useEffect(() => {
    if (payable === null) forget(invoice.id);
  }, [payable, invoice.id]);
  const [sent, setSent] = useState<Sent | null>(null);
  /** Whether she opened the ways to pay after they folded away. */
  const [unfolded, setUnfolded] = useState(false);

  const pushed = faceOf(invoice.latestFace);
  const newest = invoice.feedbackAsk;
  // Ops said it recorded nothing of her newest answer: the one this page sent, or, before she sent
  // one here, the newest she sent at all.
  const notRecorded = newest?.recorded === false && (sent === null || sent.id === newest.id);
  // The face that counts: the one she just sent here; else her newest answer about the clean, on
  // the invoice or on the clean, while ops's last push of the invoice cannot carry it yet, because
  // it waits to be sent or ops had it only after that push (provisional P47); else the one ops last
  // pushed.
  const answered = notRecorded
    ? pushed
    : (sent?.face ?? (newest?.afterPush ? faceOf(newest.face) : null) ?? pushed);

  function onSent(id: string, face: Face) {
    setSent({ id, face });
    if (belowGood(face)) setUnfolded(false);
    reload();
  }

  useEffect(() => {
    if ((toldHere || pausedHere.current) && notCharged) notChargedLine.current?.focus();
  }, [toldHere, notCharged]);

  // What the saved method will do is said while ops says it pays an invoice still owed, and no bank
  // payment on it clears, which the card says in its place; and not while ops says, by refusing her
  // taking her report back, that the invoice owes nothing now, which is said in place of it all.
  const { autopay } = invoice;
  const pays =
    savedPays &&
    !owesNothingSaid(invoice) &&
    autopay !== undefined &&
    invoice.state !== undefined &&
    OWED.has(invoice.state) &&
    !invoice.pay.clearing
      ? autopay
      : null;

  // The first read after ops refused a press: where it takes the ways to pay away, the focus goes to
  // what now says how the invoice stands, what the saved method will do or the card's state, rather
  // than falling to the page with the screen it was on.
  const waysShown = payable !== null && !held;
  // A way to pay it is drawn below the words back from Stripe's page, so home's line saying to
  // choose a way to pay stands there after `not-paid`: not where ops pushed it paid or nothing
  // owed, a payment or a report holds it on its way, ops's refusal of her newest ask leaves nothing
  // to offer, or only the office's number is offered (`WordsBack`).
  const payBelow =
    waysShown &&
    (offersWay('zelle', pay, invoice.payAsk) || offersWay('bank', pay, invoice.payAsk));
  useEffect(() => {
    const on = endedOn.current;
    if (on === null || on === invoice) return;
    endedOn.current = null;
    if (waysShown) return;
    (pays === null ? standing : savedBlock).current?.focus();
  }, [invoice, waysShown, pays]);

  const ways = useRef<HTMLDivElement>(null);
  const documentsPart = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const { state: arrivedWith, search } = useLocation();
  // A link ops put in front of her names one of the invoice's PDFs (`?document=`, P107): read once,
  // as the page is drawn. Where the invoice offers it and ops says it is made, its button presses
  // itself once, as hers would (`opening`); either way the Documents part is where the page lands.
  const [toDocuments] = useState(() =>
    documentOnArrival(new URLSearchParams(search).get(DOCUMENT_QUERY), invoice.documents),
  );
  const [opening, setOpening] = useState(toDocuments?.press);
  // The PDF's id is taken out of the address at once, and nothing else of it, so a reload or the
  // way back from the PDF never asks again; the history keeps what it held.
  useEffect(() => {
    const query = new URLSearchParams(search);
    if (!query.has(DOCUMENT_QUERY)) return;
    query.delete(DOCUMENT_QUERY);
    const rest = query.toString();
    void navigate({ search: rest === '' ? '' : `?${rest}` }, { replace: true, state: arrivedWith });
    // Once, as the page is first drawn.
  }, []);
  useEffect(() => {
    if (toDocuments !== null) return landOn(title.current, documentsPart.current);
  }, [toDocuments]);
  // A link ops put in front of her names a way to pay (`?pay=`, P107): the ways open on it, as she
  // arrives, only where that way is offered for this invoice as ops has it now, and the page lands
  // on them as Pay does; otherwise the invoice is drawn as usual. Read once, as the page is drawn.
  const [asked] = useState(() => {
    const way = wayAsked(search);
    return way !== undefined && waysShown && offersWay(way, pay, invoice.payAsk) ? way : undefined;
  });
  // She pressed Pay on Payments to come here, or followed a link that names a way offered, and the
  // ways to pay are drawn as she arrives: they are brought into view once, the page's heading
  // having the focus first (`landOn`), or a moment after a first load, where nothing takes it.
  // A way to open on, from the page Stripe's back arrow returns to, brings its own heading into view
  // instead (`WaysToPay`), unless the ways are folded away after a lesser face, where "Ways to pay
  // this invoice" is brought into view as on any landing. A link that names a PDF lands on the
  // Documents part instead.
  const [landing] = useState(
    () =>
      (cameToPay(arrivedWith) || asked !== undefined) &&
      (opensOn(arrivedWith) === undefined || belowGood(answered)) &&
      waysShown &&
      toDocuments === null,
  );
  useEffect(() => {
    if (landing) return landOn(title.current, ways.current);
  }, [landing]);
  const Title = titleTag(back);

  return (
    <Shell at="payments" payments={place.household.payments}>
      <WordsBack back={back} invoiceBelow={payBelow} />
      <div>
        {heading === null ? null : <p className={EYEBROW}>{INVOICE.eyebrow}</p>}
        <Title
          ref={title}
          className="mt-1 font-serif text-h2 font-semibold text-pretty text-text-body"
        >
          {heading ?? INVOICE.eyebrow}
        </Title>
        {home === undefined ? null : <p className="text-body text-text-body">{home}</p>}
        {invoice.number === undefined ? null : (
          <p className="text-small text-text-body">{invoiceNumbered(invoice.number)}</p>
        )}
      </div>

      <InvoiceCard invoice={invoice} onItsWay={onItsWayNow} standingRef={standing} />

      <PaymentsMade invoice={invoice} today={place.today} />

      <HowDidWeDo
        invoice={invoice}
        today={place.today}
        household={place.household.id}
        answered={answered}
        notRecorded={notRecorded}
        replyFor={pays === null ? null : chargeReply(invoice)}
        signed={signed}
        reload={reload}
        onSent={onSent}
      />

      {takenBackSaid === null ? null : (
        <div aria-live="polite">
          <p ref={takenBackLine} tabIndex={-1} className="text-body text-pretty">
            {takenBackSaid}
          </p>
        </div>
      )}

      {pays === null ? null : (
        <SavedMethodPays
          invoice={invoice}
          autopay={pays}
          household={place.household.id}
          answered={answered}
          open={unfolded}
          onToggle={() => setUnfolded((open) => !open)}
          blockRef={savedBlock}
          onPress={() => {
            pausedHere.current = true;
          }}
          onNotCharged={() => {
            setNotChargedOn(invoice);
            setToldHere(true);
            reload();
          }}
          report={reportOffer}
          reported={reportHeld ? whatSheSent : undefined}
          signed={signed}
          reload={reload}
        />
      )}

      {notCharged ? (
        <p ref={notChargedLine} tabIndex={-1} className="text-body text-pretty">
          {PAUSE_REFUSAL_WORDS.not_charged_automatically}
        </p>
      ) : null}

      {payable === null ? null : held ? (
        // What she told the office she sent, under her own report alone: another client's is said
        // on the card, and a payment back from Stripe's page has nothing to say of it.
        whatSheSent
      ) : (
        // The ways to pay, brought to the top of the screen with a little room above them when she
        // arrives by Pay, or by a link that names a way (`landOn`).
        <div ref={ways} className="scroll-mt-4">
          <Folding
            folding={belowGood(answered)}
            open={unfolded}
            onToggle={() => setUnfolded((open) => !open)}
          >
            <WaysToPay
              invoiceId={invoice.id}
              household={place.household.id}
              startOn={asked}
              pay={pay}
              refused={invoice.payAsk}
              amountCents={payable}
              onEnded={() => {
                endedOn.current = drawn.current;
              }}
              report={reportOffer}
              signed={signed}
              reload={reload}
            />
          </Folding>
        </div>
      )}

      <Documents
        invoiceId={invoice.id}
        household={place.household.id}
        documents={invoice.documents}
        signed={signed}
        reload={reload}
        opening={opening}
        onOpened={() => setOpening(undefined)}
        sectionRef={documentsPart}
      />
    </Shell>
  );
}

/**
 * "Payments on this invoice": each payment ops recorded on it, in the order the money arrived, how
 * and when it came (`paymentSaid`) and the amount applied here, or nothing where ops sent none, or
 * only payments of a kind this build has no words for.
 */
function PaymentsMade({
  invoice,
  today,
}: {
  readonly invoice: InvoicePage;
  readonly today: string;
}) {
  const headingId = useId();
  const rows = (invoice.payments ?? []).flatMap((payment) => {
    const said = paymentSaid(payment, today);
    return said === null ? [] : [{ id: payment.id, said, amountCents: payment.amountCents }];
  });
  if (rows.length === 0) return null;
  return (
    <section aria-labelledby={headingId}>
      <h2 id={headingId} className="font-serif text-h3 font-semibold text-text-body">
        {INVOICE.payments}
      </h2>
      <ul className="mt-3 rounded-card border border-border-subtle bg-surface-raised px-5">
        {rows.map((row) => (
          <li
            key={row.id}
            className="flex justify-between gap-3 border-b border-border-subtle py-3 last:border-b-0"
          >
            <p className="min-w-0 text-body text-pretty text-text-body">{row.said}</p>
            {row.amountCents === undefined ? null : (
              <p className="shrink-0 text-body font-semibold text-text-body">
                {dollars(row.amountCents)}
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * How long the page waits for its heading to take the focus before it brings the ways to pay into
 * view all the same: nothing gives the heading the focus on a page first loaded, as after a reload
 * of one Pay led to, whose history still holds `pay`.
 */
const FOCUS_WAIT_MS = 100;

/**
 * LANDING ON THE WAYS TO PAY, once she arrives by Pay on Payments (slice M2 of round four), or by a
 * link that names a way to pay; or ON THE DOCUMENTS PART, by a link that names one of the PDFs
 * (P107): that part, `part`, is brought to the top of the screen, clear of the header, which scrolls
 * away with the page, and of the nav, smoothly unless she asked for less motion. Only once the
 * page's heading, `heading`, has the focus, which `RouteFocus` gives it after every move, so that
 * focus never pulls the screen back up to it; at once where it already has it, and after a moment
 * where nothing gives it, as on a first load. The heading keeps the focus, so a screen reader says
 * where she arrived. The landing then holds the part there while the page's own layout settles,
 * until she does anything herself (`bringToTopAndHold`, after CI run 39344566493). Gives back the
 * way to stop waiting, and to let go, as the screen goes.
 */
function landOn(heading: HTMLElement | null, part: HTMLElement | null): (() => void) | undefined {
  if (heading === null || part === null) return undefined;
  let waiting: ReturnType<typeof setTimeout> | undefined;
  let letGo: (() => void) | undefined;
  function stopWaiting() {
    clearTimeout(waiting);
    heading?.removeEventListener('focus', bring);
  }
  function bring() {
    stopWaiting();
    letGo = bringToTopAndHold(part);
  }
  if (document.activeElement === heading) bring();
  else {
    heading.addEventListener('focus', bring);
    waiting = setTimeout(bring, FOCUS_WAIT_MS);
  }
  return () => {
    stopWaiting();
    letGo?.();
  };
}

/** The amount the card leads with: what is still to pay while owed, the total once paid. */
function amountOf(invoice: InvoicePage): number | null {
  const { state, balanceCents, totalCents } = invoice;
  if (state !== undefined && OWED.has(state)) return balanceCents ?? null;
  if (state === 'paid') return totalCents ?? null;
  return null;
}

/**
 * The state ops says the invoice is in, in words, or null for one with nothing to say: paid, nothing
 * owed, or, while it is owed, what its card on Payments says too (`owedStateOf`): the day it is due,
 * or, while it is on its way (`onItsWayLine`), what says so in place of that day.
 */
function stateOf(invoice: InvoicePage, onItsWay: string | null): string | null {
  const { state, charged } = invoice;
  if (state === 'paid') return charged === undefined ? PAYMENTS.paid : chargedWords(charged);
  if (state !== undefined && NOTHING_OWED.has(state)) return INVOICE.nothingOwed;
  return owedStateOf(invoice, onItsWay);
}

/**
 * What did not go through of her report that she sent it by Zelle, or of her taking it back; and
 * `not_here`, the door's 404, the invoice no longer one she may act on here.
 */
type WordProblem = 'failed' | 'offline' | 'too_many' | 'not_here';

/**
 * A refusal of her report that she sent it by Zelle, as the contract words it (section 11.7,
 * `zelleReportRefusal`), with the office's number a link where it names it; and home's own line,
 * with the phone, for one the contract gives no words.
 */
function RefusalSaid({ code }: { readonly code: string | undefined }) {
  const words = zelleReportRefusal(code);
  return words === null ? (
    <>
      {ZELLE.notToldBeforePhone} <Phone after="." />
    </>
  ) : (
    <WithPhone words={words} />
  );
}

/**
 * A refusal of her taking her report back, as the contract words it (section 11.7, "Taking it
 * back", `zelleTakeBackRefusal`), with the office's number a link where it names it; and home's own
 * line, with the phone, for one the contract gives no words and for one home gave up sending.
 */
function TakeBackRefusalSaid({ code }: { readonly code: string | undefined }) {
  const words = zelleTakeBackRefusal(code);
  return words === null ? (
    <>
      {ZELLE.notTakenBackBeforePhone} <Phone after="." />
    </>
  ) : (
    <WithPhone words={words} />
  );
}

/**
 * What did not go through, said with the phone: the connection, the door's own limits with whose
 * count and when (round six, slice S1), the invoice no longer one she may act on here, or plainly.
 */
function ProblemSaid({
  problem,
  limit,
}: {
  readonly problem: WordProblem;
  readonly limit?: TooMany;
}) {
  const said =
    problem === 'offline'
      ? PAYING.connectionDroppedBeforePhone
      : problem === 'too_many'
        ? tooManyBeforePhone(limit)
        : problem === 'not_here'
          ? INVOICE.notHereBeforePhone
          : FAILED_BEFORE_PHONE;
  return (
    <p className="mt-2 text-body text-pretty">
      {said} <Phone after="." />
    </p>
  );
}

/**
 * The day ops issued it, "Issued on Friday 16 October.", or null: where its heading already names
 * that day, ops having given no day for its clean (`daysOf`), and without a real day.
 */
function issuedOf(invoice: InvoicePage): string | null {
  const days = daysOf(invoice);
  if (days === null || !('visit' in days) || invoice.issuedOn === undefined) return null;
  const day = dayInWords(invoice.issuedOn);
  return day === null ? null : issuedOnDay(day);
}

interface CardProps {
  readonly invoice: InvoicePage;
  /**
   * What says it is on its way, in place of the day it is due (`onItsWayLine`): sent by Zelle, as
   * somebody on the household said, or back from Stripe's page in any tab; null while it is not.
   */
  readonly onItsWay: string | null;
  /** What says how it stands: its amount, its state and what is on its way. */
  readonly standingRef: Ref<HTMLDivElement>;
}

/**
 * The invoice, as ops's own invoice page carries one, in Payments' card: its amount, what its state
 * means under it (the day it is due, or her payment on its way), its lines, its total under them
 * where ops gives one, and the day it was issued at its foot.
 */
function InvoiceCard({ invoice, onItsWay, standingRef }: CardProps) {
  const amount = amountOf(invoice);
  const state = stateOf(invoice, onItsWay);
  const issued = issuedOf(invoice);
  const lines = invoice.lines.flatMap((line) =>
    line.description === undefined ? [] : [{ ...line, description: line.description }],
  );

  return (
    <article className="rounded-card border-2 border-border-brand bg-surface-brand-soft p-6">
      <div ref={standingRef} tabIndex={-1} className="flex flex-col gap-2">
        {amount === null ? null : (
          <p className="font-serif text-price font-semibold text-text-brand">{dollars(amount)}</p>
        )}
        {state === null ? null : <p className="text-body text-pretty text-text-body">{state}</p>}
        {invoice.pay.clearing ? (
          <p className="text-small text-pretty text-text-body">{PAYMENTS.clearing}</p>
        ) : null}
      </div>
      {lines.length > 0 ? (
        <>
          <ul className="mt-4 flex flex-col gap-1 border-t border-border-subtle pt-3">
            {lines.map((line, index) => (
              <li key={index} className="flex justify-between gap-3">
                <span className="min-w-0 text-small wrap-break-word text-text-body">
                  {line.description}
                </span>
                {line.amountCents === undefined ? null : (
                  <span className="shrink-0 text-small font-semibold text-text-body">
                    {dollars(line.amountCents)}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {invoice.totalCents === undefined ? null : (
            <div className="mt-3 flex justify-between gap-3 border-t border-border-subtle pt-3">
              <p className="text-body font-semibold text-text-body">{INVOICE.total}</p>
              <p className="shrink-0 text-body font-semibold text-text-body">
                {dollars(invoice.totalCents)}
              </p>
            </div>
          )}
        </>
      ) : null}
      {issued === null ? null : <p className="mt-4 text-small text-text-muted">{issued}</p>}
    </article>
  );
}

/** The check drawn on the chosen area, as on Feedback. */
function Check() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4.5 10.5 8.5 14.5 15.5 6" />
    </svg>
  );
}

/**
 * What the page says after an answer that was not sent: a code in the line; ops already holds its
 * twelve answers from home on the invoice (`409 answers_used_up`); past the door's own limits; or a
 * failure.
 */
type Problem = 'code' | 'answered' | 'too_many' | 'failed';

/**
 * What each problem with the phone after it says before the phone, past the door's own limits with
 * whose count and when, as the door said them (round six, slice S1).
 */
function beforePhone(problem: Exclude<Problem, 'code'>, limit: TooMany | undefined): string {
  switch (problem) {
    case 'answered':
      return INVOICE_FEEDBACK.alreadyBeforePhone;
    case 'too_many':
      return tooManyBeforePhone(limit);
    default:
      return FAILED_BEFORE_PHONE;
  }
}

interface HowProps {
  readonly invoice: InvoicePage;
  /** The business's day the place was read on, which the re-clean promise is drawn by. */
  readonly today: string;
  /** The household shown, which the request names. */
  readonly household: string;
  /** The face that counts: the one just sent, else ops's. */
  readonly answered: Face | null;
  /** Ops recorded nothing of her newest answer. */
  readonly notRecorded: boolean;
  /**
   * What is said of the saved method's charge once a face counts, in place of the plain thanks and
   * after the line that says ops recorded nothing of her newest answer; null where none is, and
   * null for a face it says nothing of, Good, where the plain thanks stand (`chargeReply`).
   */
  readonly replyFor: ((face: Face) => string | null) | null;
  readonly signed: Signed;
  readonly reload: () => void;
  readonly onSent: (id: string, face: Face) => void;
}

/**
 * What "How did we do?" says under the faces once a face counts: what it means for the saved
 * method's charge where `replyFor` says it, else the plain thanks, after a face below Good that the
 * office will put it right. Good is thanked plainly whether or not she named an area, as the seam
 * words it for a good face, and with no word of the saved method's charge, which a good face that
 * names an area holds (ops's section 4 at 9f10a8b): what the page says then is ops's section 5's.
 */
function thanksFor(face: Face, replyFor: ((face: Face) => string | null) | null): string {
  const reply = replyFor === null ? null : replyFor(face);
  if (reply !== null) return reply;
  return belowGood(face) ? INVOICE_FEEDBACK.putRight : INVOICE_FEEDBACK.thanks;
}

/** The thanks for an answer sent from this page, and which send it thanks. */
interface Spoken {
  readonly send: number;
  readonly text: string;
}

/**
 * "How did we do?": the four faces, and after any but Very good the area, the line, the re-clean
 * promise while it holds, and Send, as Feedback draws them (`asksWhatWasMissed`,
 * `recleanPromised`), brought into view by the tap that opened them, clear of the nav. A face tapped
 * is drawn chosen, a ring and no fill, and described to a screen reader as not sent, until Send; a
 * face sent, or the one ops says counts, fills. Under them,
 * what the face that counts means, which stands outside the live region, since it changes with the
 * stage ops says and a screen reader must not hear that as a thanks; only the thanks for an answer
 * sent from this page is drawn in the live region, a new line for each send, so it is read out once,
 * and only until what it says changes.
 */
function HowDidWeDo({
  invoice,
  today,
  household,
  answered,
  notRecorded,
  replyFor,
  signed,
  reload,
  onSent,
}: HowProps) {
  const headingId = useId();
  const tapId = useId();
  const notSentId = useId();
  const areaId = useId();
  const lineId = useId();
  const problemId = useId();
  const areaGroup = useId();
  const field = useRef<HTMLTextAreaElement>(null);
  const thanksLine = useRef<HTMLParagraphElement>(null);
  const asking = useRef<HTMLFormElement>(null);
  /** A face that asks what was missed, which she has tapped and not yet sent. */
  const [choosing, setChoosing] = useState<Face | null>(null);
  /** Counts her taps on a face that asks more: each brings the question into view. */
  const [asked, setAsked] = useState(0);
  /** The label of the area chosen. */
  const [chip, setChip] = useState<string | null>(null);
  const [line, setLine] = useState('');
  const [problem, setProblem] = useState<Problem | null>(null);
  /** Past the door's own limits: whose count and when, as the door said them (slice S1). */
  const [limit, setLimit] = useState<TooMany>();
  /** Counts the answers sent by Send, so the thanks takes the focus the Send button had. */
  const [formSent, setFormSent] = useState(0);
  /** The thanks for the answer last sent from this page, while it still says the same. */
  const [spoken, setSpoken] = useState<Spoken | null>(null);
  /**
   * Whether the answer last sent from this page named what was missed (`areaSent`), so the office
   * will be in touch to put it right (round six, slice S3).
   */
  const [named, setNamed] = useState(false);
  /** Counts the answers sent from this page, so each thanks is a new line in the live region. */
  const sends = useRef(0);
  /** What the charge means for a face now, read when a send comes back. */
  const reply = useRef(replyFor);
  reply.current = replyFor;
  /** A request is out, so another tap does nothing. */
  const out = useRef(false);
  const mounted = useRef(true);
  /** The key minted for the answer it was minted for. */
  const minted = useRef<{ readonly answer: string; readonly key: string } | null>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (formSent > 0) thanksLine.current?.focus();
  }, [formSent]);

  useEffect(() => {
    if (asked > 0) bringIntoView(asking.current);
  }, [asked]);

  async function send(face: Face, area: string | null, said: string) {
    if (out.current) return;
    out.current = true;
    setProblem(null);
    const payload = {
      invoice_id: invoice.id,
      face,
      ...(area === null ? {} : { area }),
      ...(said.trim() === '' ? {} : { line: said }),
    };
    const answer = JSON.stringify(payload);
    if (minted.current?.answer !== answer) minted.current = { answer, key: crypto.randomUUID() };
    const outcome = await sendRequest({
      key: minted.current.key,
      type: 'invoice.feedback',
      household,
      payload,
    });
    out.current = false;
    if (!mounted.current) return;
    switch (outcome.outcome) {
      case 'sent':
        setChoosing(null);
        setChip(null);
        setLine('');
        sends.current += 1;
        setSpoken({ send: sends.current, text: thanksFor(face, reply.current) });
        setNamed(area !== null);
        if (asksWhatWasMissed(face)) setFormSent((count) => count + 1);
        onSent(outcome.id, face);
        return;
      case 'signed_out':
        signed.onSignedOut();
        return;
      case 'not_found':
        setProblem('failed');
        reload();
        return;
      case 'looks_like_a_code':
        setProblem('code');
        field.current?.focus();
        return;
      case 'too_many':
        setLimit(outcome.limit);
        setProblem('too_many');
        return;
      case 'refused':
        setProblem(outcome.code === 'answers_used_up' ? 'answered' : 'failed');
        return;
      default:
        setProblem('failed');
    }
  }

  function tapped(face: Face) {
    if (out.current) return;
    if (asksWhatWasMissed(face)) {
      setChoosing(face);
      setProblem(null);
      setAsked((count) => count + 1);
      return;
    }
    setChoosing(null);
    void send(face, null, '');
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (choosing === null) return;
    // A named chip, else "Other" for her line, under Other or alone (`areaSent`, ops #72).
    void send(choosing, areaSent(chip, line), line);
  }

  const shown = choosing ?? answered;
  // What the face that counts means for the charge, under the line that ops recorded nothing.
  const standingReply = replyFor === null || answered === null ? null : replyFor(answered);
  const thanks =
    answered === null || choosing !== null || problem !== null
      ? null
      : thanksFor(answered, replyFor);
  // The thanks for a send is spoken only while it says what it said then: once the stage ops says
  // changes it, or a new tap takes it away, it stands outside the live region, and is not read out.
  if (spoken !== null && thanks !== spoken.text) setSpoken(null);
  const speaking = spoken !== null && thanks === spoken.text ? spoken : null;
  // After an answer that named what was missed, what happens next, under the thanks, unless ops's
  // words for the face say it already (round six, slice S3).
  const inTouch =
    named && thanks !== null && thanks !== INVOICE_FEEDBACK.putRight ? (
      <p className="mt-2 text-body text-pretty">{FEEDBACK.inTouch}</p>
    ) : null;

  return (
    <section aria-labelledby={headingId}>
      <h2 id={headingId} className="font-serif text-h3 font-semibold text-text-body">
        {INVOICE_FEEDBACK.heading}
      </h2>

      {notRecorded ? (
        <>
          <p className="mt-2 text-body text-pretty">
            {INVOICE_FEEDBACK.alreadyBeforePhone} <Phone after="." />
          </p>
          {standingReply === null ? null : (
            <p className="mt-2 text-body text-pretty">{standingReply}</p>
          )}
        </>
      ) : (
        <>
          <p id={tapId} className="mt-1 text-small text-pretty text-text-muted">
            {INVOICE_FEEDBACK.tap}
          </p>
          <div
            role="group"
            aria-labelledby={headingId}
            aria-describedby={tapId}
            className="mt-4 grid grid-cols-4 gap-1"
          >
            {FACES.map(({ face, label }) => {
              const on = shown === face;
              // Tapped and waiting for Send: chosen, not sent.
              const waiting = choosing === face;
              return (
                <button
                  key={face}
                  type="button"
                  aria-pressed={on}
                  aria-describedby={waiting ? notSentId : undefined}
                  onClick={() => tapped(face)}
                  className="flex cursor-pointer flex-col items-center gap-1 rounded-control py-1"
                >
                  <span
                    className={`flex size-15 items-center justify-center rounded-full border-2 ${
                      waiting ? FACE_CHOSEN : on ? FACE_SENT : FACE_FREE
                    }`}
                  >
                    <FaceIcon face={face} />
                  </span>
                  <span
                    className={`text-center text-small whitespace-nowrap text-text-body ${on ? 'font-semibold' : ''}`}
                  >
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
          {choosing === null ? null : (
            <span id={notSentId} className="sr-only">
              {INVOICE_FEEDBACK.notSent}
            </span>
          )}

          {choosing === null ? null : (
            <form
              ref={asking}
              method="post"
              onSubmit={submit}
              className={`mt-5 flex flex-col ${CLEAR_OF_THE_NAV}`}
            >
              <p id={areaId} className={QUESTION}>
                {FEEDBACK.whichArea}
              </p>
              <div
                role="radiogroup"
                aria-labelledby={areaId}
                className="mt-2.5 flex flex-wrap gap-2"
              >
                {AREAS.map(({ label }) => {
                  const on = chip === label;
                  return (
                    <label
                      key={label}
                      className={`${CHIP} ${on ? CHIP_CHOSEN : CHIP_FREE} ${RING}`}
                    >
                      <input
                        type="radio"
                        name={areaGroup}
                        value={label}
                        checked={on}
                        onChange={() => setChip(label)}
                        className="sr-only"
                      />
                      {on ? <Check /> : null}
                      <span>{label}</span>
                    </label>
                  );
                })}
              </div>

              <label htmlFor={lineId} className={`mt-5 ${QUESTION}`}>
                {FEEDBACK.whatWasMissed}
              </label>
              <textarea
                ref={field}
                id={lineId}
                value={line}
                onChange={(event) => setLine(event.target.value)}
                rows={1}
                maxLength={FEEDBACK_LINE_MAX}
                aria-invalid={problem === 'code' ? true : undefined}
                aria-describedby={problem === null ? undefined : problemId}
                className="mt-2 field-sizing-content max-h-48 min-h-tap-call resize-none rounded-control border-2 border-border-strong bg-surface-raised px-4 py-3 text-body text-text-body"
              />

              {recleanPromised(invoice.visitDate, today) ? (
                <p className="mt-4 rounded-card border border-border-subtle bg-surface-brand-soft px-4 py-3.5 text-small text-pretty text-text-body">
                  {FEEDBACK.noCharge}
                </p>
              ) : null}

              <button type="submit" className={`mt-4 ${NAVY_BUTTON}`}>
                {FEEDBACK.send}
              </button>
            </form>
          )}

          <div aria-live="polite" id={problemId}>
            {problem === null ? null : (
              <p className="mt-4 text-body text-pretty">
                {problem === 'code' ? (
                  LOOKS_LIKE_A_CODE
                ) : (
                  <>
                    {beforePhone(problem, limit)} <Phone after="." />
                  </>
                )}
              </p>
            )}
          </div>

          <div aria-live="polite">
            {speaking === null ? null : (
              <p
                key={speaking.send}
                ref={thanksLine}
                tabIndex={-1}
                className="mt-4 text-body text-pretty"
              >
                {speaking.text}
              </p>
            )}
            {speaking === null ? null : inTouch}
          </div>
          {thanks === null || speaking !== null ? null : (
            <>
              <p ref={thanksLine} tabIndex={-1} className="mt-4 text-body text-pretty">
                {thanks}
              </p>
              {inTouch}
            </>
          )}
        </>
      )}
    </section>
  );
}

/** Which screen of the ways to pay she is on: Zelle, the bank or the card. */
type Way = 'zelle' | 'bank' | 'card';

/**
 * What ops refused a press with that leaves nothing to offer here, said in place of every way to
 * pay: a bank payment on its way, nothing owed now, or her saved method paying it, for which home
 * draws no bank or card screen (the seam, l.619) and Zelle would be paid as well as the charge.
 */
type Ended = 'clearing' | 'not_open' | 'saved';

/** What each refusal that leaves nothing to offer says in place of the ways to pay. */
const ENDED: Readonly<Partial<Record<Ending, Ended>>> = {
  bank_payment_clearing: 'clearing',
  invoice_not_open: 'not_open',
  saved_method_pays: 'saved',
};

/** Ops's refusal of her newest ask, as the Worker carries it, where it changes what is offered. */
const endingOf = (ask: PayAsk | undefined): Ending | null =>
  ask?.state === 'refused' ? (ENDINGS.find((each) => each === ask.code) ?? null) : null;

/**
 * Whether the ways to pay, drawn now, offer `way` (P107): Zelle where ops sent its tag, the bank
 * and the card while ops says those screens exist and has not refused her newest ask as Stripe off;
 * none where ops refused it with what leaves nothing to offer, which is said in their place.
 */
function offersWay(way: WayToPay, pay: PagePay, refused: PayAsk | undefined): boolean {
  const ending = endingOf(refused);
  if (ending !== null && ENDED[ending] !== undefined) return false;
  if (way === 'zelle') return pay.zelle !== undefined;
  return pay.online && ending !== 'payments_off';
}

interface WaysProps {
  readonly invoiceId: string;
  /** The household shown, which a request names. */
  readonly household: string;
  /**
   * The way a link named, where it is offered (`?pay=`, P107): the ways start on it, with no focus
   * given, as the page lands on them; the way the history names comes first.
   */
  readonly startOn?: WayToPay;
  readonly pay: PagePay;
  /** Her newest ask to pay it, carried while ops's refusal of it is newer than the push. */
  readonly refused: PayAsk | undefined;
  /** What is still to pay from here, as ops reads it. */
  readonly amountCents: number;
  /** Ops refused a press with what changes the ways to pay, and the invoice is read again. */
  readonly onEnded: () => void;
  /** "I sent it by Zelle", in the card with Zelle's tag (section 11.7, P53). */
  readonly report: ReportOffer;
  readonly signed: Signed;
  readonly reload: () => void;
}

/**
 * The ways to pay the invoice from here, one screen at a time, held here and never in the address:
 * Zelle first, then, only while ops says the bank and card screens exist, "Pay another way" to the
 * bank and on to the card, each with the way back. Without a Zelle tag from ops they start at the
 * bank, and with neither, the office's number. Opened by the way back from Stripe's page, they
 * start on the way she left from (`opensOn`), as far as ops still offers it; and loaded afresh
 * because ops said the words beside her tick had changed (round five, slice A4), on the way she
 * pressed on, whatever way the history still holds, whose screen says so with the focus, until she
 * moves to another way. Opened by a link that names a way offered (`startOn`, P107), they start on
 * it with no focus given, the page landing on them as by Pay. Each move gives the focus to the screen moved to and brings its heading to
 * the top of the screen, clear of the nav, as opening on a way does, so the new way's Pay is in view
 * (round six, slice S1); and only a move or a press does, so the page drawn first takes no focus.
 *
 * What ops refuses a press with is kept here, so it holds until ops's push says how the invoice
 * stands: Stripe off leaves Zelle alone, as ops saying so on a push does; a bank payment clearing,
 * nothing owed and the saved method paying it each say so in place of every way to pay; and every
 * page made puts the office's number in place of Pay on the bank and the card. The same holds from
 * ops's refusal of her newest ask, which the Worker carries until the push catches up with it, so
 * it holds where the ways to pay are drawn afresh.
 */
function WaysToPay({
  invoiceId,
  household,
  startOn,
  pay,
  refused,
  amountCents,
  onEnded,
  report,
  signed,
  reload,
}: WaysProps) {
  /**
   * The way the page was loaded afresh on because ops said the words beside her tick had changed
   * (slice A4, `lib/paying.ts`), read once as the ways are drawn and forgotten once said; it goes as
   * she moves to another way.
   */
  const [changedOn, setChangedOn] = useState(() => termsChangedOn(invoiceId));
  useEffect(() => {
    if (changedOn !== undefined) forgetTermsChanged();
  }, [changedOn]);
  // The way she pressed on is the newer word: a load afresh keeps the history entry, and with it any
  // way a link handed the page to open on, as How you pay's link to the bank does, which would
  // otherwise open her on a way she had left (the review of slice A4).
  const opened = changedOn ?? opensOn(useLocation().state);
  const [way, setWay] = useState<Way>(opened ?? startOn ?? 'zelle');
  const carried = endingOf(refused);
  /** Ops refused a press because Stripe is off, whatever the copy still says. */
  const [offHere, setOff] = useState(false);
  const off = offHere || carried === 'payments_off';
  /** Ops has made every page it will for the invoice from home. */
  const [usedUpHere, setUsedUp] = useState(false);
  const usedUp = usedUpHere || carried === 'pay_pages_used_up';
  const [endedHere, setEnded] = useState<Ended | null>(null);
  const ended = endedHere ?? (carried === null ? null : (ENDED[carried] ?? null));
  /**
   * She has moved between the screens, or a press changed them, or they opened on the way she left
   * from (slice M2 of round four, after the walk was replayed, where that way's heading opened out of
   * sight under the nav), so what is shown takes the focus.
   */
  const moved = useRef(opened !== undefined);
  /**
   * They opened on the way she left from, so its heading, given the focus without the browser's own
   * scroll, is brought to the top of the screen, clear of the nav, once.
   */
  const openedOn = useRef(opened !== undefined);
  const zelleHeading = useRef<HTMLHeadingElement>(null);
  const bankHeading = useRef<HTMLHeadingElement>(null);
  const cardHeading = useRef<HTMLHeadingElement>(null);
  /** The line that says the words beside her tick changed, where the screen opened on draws it. */
  const changedLine = useRef<HTMLParagraphElement>(null);
  const phoneLine = useRef<HTMLParagraphElement>(null);
  const endedLine = useRef<HTMLParagraphElement>(null);
  const { zelle } = pay;
  const online = pay.online && !off;
  // The bank and the card exist only while ops says so; once it says not, she is on Zelle, and
  // stays there should ops later say they exist again. Without Zelle they start at the bank.
  const at: Way | null = online
    ? way === 'zelle' && zelle === undefined
      ? 'bank'
      : way
    : zelle === undefined
      ? null
      : 'zelle';
  if (at !== null && at !== way) setWay(at);

  useEffect(() => {
    if (!moved.current) return;
    if (ended !== null) {
      endedLine.current?.focus();
      return;
    }
    const headings = { zelle: zelleHeading, bank: bankHeading, card: cardHeading };
    const shown = (at === null ? phoneLine : headings[at]).current;
    if (openedOn.current) {
      openedOn.current = false;
      // Opened afresh because the words beside her tick changed: the line that says so takes the
      // focus, read first, with the way's heading brought to the top of the screen above it.
      (changedLine.current ?? shown)?.focus({ preventScroll: true });
      bringToTop(shown);
      return;
    }
    // A move brings what is shown to the top of the screen as opening on it does, its focus given
    // without the browser's own scroll to the middle, which left the new way's Pay under the nav on
    // a phone (round six, slice S1, the replay of 2026-10-18); where the way down to its Pay is
    // taller than the screen, its Pay is brought into view instead, just above the nav.
    shown?.focus({ preventScroll: true });
    bringScreenToTop(shown, shown?.closest('section')?.querySelector('[data-pay]') ?? null);
  }, [at, ended]);

  function go(next: Way) {
    moved.current = true;
    setChangedOn(undefined);
    setWay(next);
  }

  function onEnding(ending: Ending) {
    moved.current = true;
    onEnded();
    switch (ending) {
      case 'payments_off':
        setOff(true);
        return;
      case 'pay_pages_used_up':
        setUsedUp(true);
        return;
      default:
        // A bank payment clearing, nothing owed, or the saved method paying it, each said in place
        // of every way to pay; once ops's push says the saved method pays it, the page says what
        // it will do instead.
        setEnded(ENDED[ending] ?? null);
    }
  }

  if (ended === 'saved') {
    return (
      <p ref={endedLine} tabIndex={-1} className="text-body text-pretty">
        {PAYING.savedMethodPays} {SAVED_METHOD_PAYS.alreadySentBeforePhone} <Phone after="." />
      </p>
    );
  }
  if (ended !== null) {
    return (
      <p ref={endedLine} tabIndex={-1} className="text-body text-pretty">
        {ended === 'clearing' ? PAYMENTS.clearing : PAYING.nothingToPay}
      </p>
    );
  }
  if (at === null) return <PayByPhone lineRef={phoneLine} />;
  if (at === 'zelle' && zelle !== undefined) {
    return (
      <ZelleWay
        amountCents={amountCents}
        zelle={zelle}
        onAnotherWay={online ? () => go('bank') : undefined}
        report={report}
        headingRef={zelleHeading}
      />
    );
  }
  const onBank = at !== 'card';
  return (
    <StripeWay
      key={onBank ? 'bank' : 'card'}
      kind={onBank ? 'bank' : 'card'}
      invoiceId={invoiceId}
      household={household}
      amountCents={amountCents}
      offerToSave={pay.offerToSave}
      usedUp={usedUp}
      onEnding={onEnding}
      signed={signed}
      reload={reload}
      headingRef={onBank ? bankHeading : cardHeading}
      onNext={onBank ? () => go('card') : undefined}
      onBack={onBank ? (zelle === undefined ? undefined : () => go('zelle')) : () => go('bank')}
      termsChanged={changedOn === (onBank ? 'bank' : 'card')}
      termsChangedRef={changedLine}
    />
  );
}

/** The office's number, to pay the invoice by: `lineRef` is given where a move focuses it. */
function PayByPhone({ lineRef }: { readonly lineRef: Ref<HTMLParagraphElement> }) {
  return (
    <p ref={lineRef} tabIndex={-1} className="scroll-mt-4 text-body text-pretty">
      {INVOICE.payBeforePhone} <Phone after="." />
    </p>
  );
}
